import { CommunityFilter } from '../model/CommunityFilter';
import { Dispatch, useCallback, useReducer } from 'react';
import { PlaceData } from '../model/Places';
import { getFilter } from '../service/CommunityFilterService';

export type AgeRangePayload = {
  ageRangeStartInMonths: number;
  ageRangeEndInMonths: number;
};

export type CommunityFilterStore = {
  isDraft: boolean;
  filters: CommunityFilter;
};

type FetchCommunityFilter = {
  type: 'FETCH_COMMUNITY_FILTER';
  payload: CommunityFilter;
};

type ResetFilter = {
  type: 'RESET_FILTER';
  payload: CommunityFilter;
};

type ApplyFilter = {
  type: 'APPLY_FILTER';
};

type ChangeDistance = {
  type: 'CHANGE_DISTANCE';
  payload: number;
};

type ChangeAgeRange = {
  type: 'CHANGE_AGE_RANGE';
  payload: AgeRangePayload;
};

type ChangeLocation = {
  type: 'CHANGE_LOCATION';
  payload: PlaceData;
};

export type FilterAction =
  | FetchCommunityFilter
  | ResetFilter
  | ApplyFilter
  | ChangeDistance
  | ChangeAgeRange
  | ChangeLocation;

export const MAX_AGE_RANGE_IN_MONTHS = 216; // 12 * 18yr
export const MIN_AGE_RANGE_IN_MONTHS = 0;

const DEFAULT_COMMUNITY_FILTER: CommunityFilterStore = {
  isDraft: true,
  filters: {
    locationData: null,
    distanceInMiles: 30,
    ageRangeStartInMonths: MIN_AGE_RANGE_IN_MONTHS,
    ageRangeEndInMonths: MAX_AGE_RANGE_IN_MONTHS,
  },
};

export const useCommunityFilter = () => {
  const [filterStore, filterDispatch] = useReducer(
    reducer,
    DEFAULT_COMMUNITY_FILTER,
  );
  return { filterStore, filterDispatch };
};

const reducer = (
  store: Readonly<CommunityFilterStore>,
  action: FilterAction,
): CommunityFilterStore => {
  switch (action.type) {
    case 'FETCH_COMMUNITY_FILTER':
      return setCommunityFilter(store, action.payload);
    case 'RESET_FILTER':
      return resetFilter(store, action.payload);
    case 'APPLY_FILTER':
      return applyFilter(store);
    case 'CHANGE_DISTANCE':
      return updateDistanceFilter(store, action.payload);
    case 'CHANGE_LOCATION':
      return updateLocationFilter(store, action.payload);
    case 'CHANGE_AGE_RANGE':
      return updateAgeRangeFilter(store, action.payload);
    default:
      throw Error(`Unsupported action ${action}`);
  }
};

const setCommunityFilter = (
  store: Readonly<CommunityFilterStore>,
  communityFilter: CommunityFilter,
) => ({ isDraft: false, filters: communityFilter });

const resetFilter = (
  store: Readonly<CommunityFilterStore>,
  defaultCommunityFilter: CommunityFilter,
) => ({ filters: defaultCommunityFilter, isDraft: true });

const applyFilter = (store: Readonly<CommunityFilterStore>) => ({
  ...store,
  isDraft: false,
});

const updateDistanceFilter = (
  store: Readonly<CommunityFilterStore>,
  newDistanceInMiles: number,
) => {
  const updatedFilters = {
    ...store.filters,
    distanceInMiles: newDistanceInMiles,
  };
  return { filters: updatedFilters, isDraft: true };
};

const updateLocationFilter = (
  store: Readonly<CommunityFilterStore>,
  newLocation: PlaceData,
) => {
  const updatedFilters = { ...store.filters, locationData: newLocation };
  return { filters: updatedFilters, isDraft: true };
};

const updateAgeRangeFilter = (
  store: Readonly<CommunityFilterStore>,
  ageRange: AgeRangePayload,
) => {
  const updatedFilters = {
    ...store.filters,
    ageRangeStartInMonths: ageRange.ageRangeStartInMonths,
    ageRangeEndInMonths: ageRange.ageRangeEndInMonths,
  };

  return {
    filters: updatedFilters,
    isDraft: true,
  };
};

export const useFetchCommunityFilter = (
  communityFilterDispatch: Dispatch<FilterAction>,
) => {
  return useCallback(async () => {
    const communityFilter = await getFilter();
    communityFilterDispatch({
      type: 'FETCH_COMMUNITY_FILTER',
      payload: communityFilter,
    });
  }, [communityFilterDispatch]);
};
